import React, { useEffect, useState, useContext, useMemo, useRef } from "react";
import Joyride, { Step, CallBackProps, STATUS, ACTIONS } from 'react-joyride';
import Table from "../components/Table";
import NLPSearch, { SuggestionItem } from "../components/NLPSearch"; // 1. Updated Import
import Button from "../components/Button";
import { useNavigate } from "react-router-dom";
import Spinner from "../components/Spinner";
import localStorageService from "../services/localStorageService";
import NlpSearchService from "../services/NlpSearchService";
import Pagination from "../components/Pagination";
import NoDataFound from "../components/NoDataFound";
import { AuthContext } from "../store/ContextStore";
import Datepicker from "../components/Datepicker";
import MultiSelectDropdown from "../components/MultiSelectDropdown";
import SearchWithSuggestions from "../components/SearchWithSuggestion";
import Modal from "../components/modals/Modal";
import { AxiosResponse } from "axios";
import { useReactiveRef } from "../utils/UseReactiveState";
import { exportToXLSX } from "../utils/xlsx";
// import GraphPopupModal from "../components/GraphPopupModal";
import GraphPopupModal, { VisualizationSpinner } from "../components/graphs/GraphPopupModal";

import Cookies from "js-cookie";
import { TYPE_AHEAD_SUGGESTION } from "../constants/routes"; // 2. Added Import

const gameResults = [
  { name: "WIN", code: "1" },
  { name: "LOSS", code: "0" },
];

export interface Metadata {
  ID: number;
  CLASS: string;
  POS?: string;
  DATE: string;
  OPP: string;
  RES: string;
  PLAYER: string;
  TEAM: string;
  USABLE_PERIODS?: number;
  STATS_THAT_DO_NOT_MATCH?: string[] | string;
  PERIOD_NUMBER: number;
}

export interface Stats {
  RUSHYARDS: string;
}

export interface Result {
  metadata: Metadata;
  stats: Stats;
}

export interface AQLQualifiers {
  teamName: string;
}

export interface AQLConditions {
  conditions: string;
  qualifiers: AQLQualifiers;
  stat_period: string;
  streak_len: number | null;
}

export interface AQLOutput {
  query_type: string;
  conditions: string;
  qualifiers: AQLQualifiers;
  stat_period: string;
  streak_len: number | null;
}

export interface Response {
  count: number;
  results: Result[];
  aql_output: AQLOutput;
  sql_query: string;
  inference_time: number;
  period_type: string[];
  positions: string[];
  classes: string[];
  teams: string[];
  game_type: string[];
  conferences: {
    [key: string]: {
      conference_short_name: string;
      teams_associated_with_conference: string[];
    }
  };
  anchor_keys?: any[];
}

interface SortFields {
  sortBy: string | null;
  sortOrder: string | null;
}

export default function ResultScreen() {
  const [searchString, setSearchString] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const { selectedSport, user, team, openToast } = useContext(AuthContext);
  const [results, setResults] = useState<Result[]>([]);
  const [data, setData] = useState<Response | null>(null);
  const dashboard_Service = new NlpSearchService();
  const [loading, setLoading] = useState<boolean>(false);
  const [filter_config, setfilter_config] = useState<any>(null);
  const [selectedFilters, setSelectedFilters, selectedFiltersRef] = useReactiveRef<any>({});
  const [appliedFilters, setAppliedFilters] = useState<any>({});
  const [resultsLoaded, setResultsLoaded] = useState(false);


  // 3. TYPE-AHEAD STATE
  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([]);

  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [errorStatus, setErrorStatus] = useState<number | null>(null);

  const [sortFields, setSortFields] = useState<SortFields>({
    sortBy: null,
    sortOrder: null,
  });
  const filterRef = useRef<HTMLDivElement>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const isMounted = useRef(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  

  const [isGraphPopupOpen, setIsGraphPopupOpen] = useState(false);
  const [visualizationData, setVisualizationData] = useState<any>(null);
  const [vizLoading, setVizLoading] = useState(false);


  const [isDisabled, setIsDisabled] = useState(false);
  const [error, setError] = useState("");

  const [issue, setIssue] = useState("");
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [feedbackType, setFeedbackType] = useState<'success' | 'error' | null>(null);
  const navigate = useNavigate();

  // 4. TYPE-AHEAD LOGIC (With Zero-State History)
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!user?.username) return;

      const currentEntity = filter_config?.entity || filter_config?.Entity || 'Player';

      if (searchString.length < 3) {
        setSuggestions([]);
        return;
      }

      try {
        const url = `${TYPE_AHEAD_SUGGESTION}/?sport_code=${selectedSport}&entity=${currentEntity}&q=${encodeURIComponent(
          searchString
        )}&limit=10&user_id=${encodeURIComponent(user.username)}`;

        const response = await fetch(url);

        if (response.ok) {
          const data = await response.json();
          setSuggestions(data.suggestions || []);
        }
      } catch (error) {
        console.error("Error fetching type-ahead suggestions:", error);
      }
    };

    const debounceTimer = setTimeout(() => {
      fetchSuggestions();
    }, 0);

    return () => clearTimeout(debounceTimer);
  }, [searchString, selectedSport, filter_config, user]);


  // --- Joyride State and Steps for ResultScreen ---
  const [runResultTour, setRunResultTour] = useState<boolean>(false);
  const resultTourSteps: Step[] = useMemo(() => [
    {
      target: '#tour-filter-opponent',
      content: 'Now that you have results, try selecting a filter. For example, choose an opponent or conference.',
      disableBeacon: true,
      placement: 'right',
      styles: {
        tooltip: {
          borderRadius: '1rem'
        },
      },
      showSkipButton: false,
      hideCloseButton: true,
    },
    {
      target: '#tour-apply-filters-button',
      content: 'Great! Now click here to apply your selected filters and see how the results change.',
      disableBeacon: true,
      placement: 'top',
      styles: {
        tooltip: {
          borderRadius: '1rem'
        },
      },
      hideCloseButton: true,
    },
  ], []);

  const handleResultJoyrideCallback = (data: CallBackProps) => {
    const { status } = data;

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunResultTour(false);
      const tourCookieName = `resultTour_${user?.username}`; // Make it user-specific
      Cookies.set(tourCookieName, "true", { expires: 365 });
      localStorage.setItem('searchTourCompleted', 'true');
      localStorage.removeItem('tourStep');
      console.log('Result tour completed and saved to cookies');
    }
  };


  const getOpponentOptions = () => {
    if (selectedFilters?.CONF?.length > 0) {
      const teamsSet = new Set();

      Object.entries(data?.conferences ?? {}).forEach(([key, value]) => {
        if (selectedFilters.CONF.includes(key)) {
          (value?.teams_associated_with_conference ?? []).forEach(team => {
            teamsSet.add(team);
          });
        }
      });

      return Array.from(teamsSet);
    }

    // Always deduplicate teams even when no conference is selected
    return [...new Set(data?.teams ?? [])];
  };



  const onPageChange = async (page: number) => {
    setCurrentPage(page);
    if (filter_config) {
      let newConfig = {
        ...filter_config,
        PageNumber: Number(page),
        PageSize: Number(itemsPerPage),
      };
      getResults(newConfig, appliedFilters, sortFields);
      setfilter_config(newConfig);
    }
  };


  const onItemsPerPageChange = async (val: number) => {
    setItemsPerPage(val);
    setCurrentPage(1);
    if (filter_config) {
      let newConfig = {
        ...filter_config,
        PageNumber: Number(1),
        PageSize: Number(val),
      };
      getResults(newConfig, appliedFilters, sortFields);
      setfilter_config(newConfig);
    }
  };

  const shouldShowFilter = (filterType: string, stat_period: string | undefined): boolean => {
    // For game-level queries, show all filters (including OPP, CONF, RES, DATE)
    if (stat_period === "game") {
      return true;
    }
    // console.log("stat_period: ", stat_period);
    // For season-level and career-level queries, hide opponent and result filters
    // Hide: OPP, CONF, RES, DATE (these are player-specific comparisons across games)
    if (stat_period === "season" || stat_period === "career") {
      const hiddenFilters = ["OPP", "CONF", "RES", "DATE_MIN", "DATE_MAX"];
      return !hiddenFilters.includes(filterType);
    }

    // Default: show all filters
    return true;
  };

  const getResults = async (config: any, filters: any, sorting: any, isSearch: boolean = false) => {
    if (config.PageNumber) {
      setCurrentPage(config.PageNumber);
    }
    setLoading(true);
    setResultsLoaded(false); // add this
    setVisualizationData(null); // add this
    setResults([]);
    setData(null);
    setTotalPages(0);
    let searchQuery = searchString ? searchString : config.searchString;

    // Ensure TeamCode is a valid string
    const teamCode = team?.code ? String(team.code) : "";

    let payload = {
      "UserId": undefined,
      "SportCode": selectedSport,
      "TeamCode": teamCode, // Use the sanitized value
      "BasicQuery": searchQuery,
      "Entity": config["entity"],
      "GamePeriod": config["selectedQuarter"],
      "PageNumber": config["PageNumber"] ? config["PageNumber"] : Number(currentPage),
      "PageSize": config["PageSize"] ? config["PageSize"] : Number(itemsPerPage),
      "Filters": filters,
      "SortBy": sorting.sortBy,
      "SortOrder": sorting.sortOrder || null,
      "aql_output": isSearch ? null : data?.aql_output,
    }

    try {
      const response = await dashboard_Service.getSearchResults(payload);
      if (response["status"] === 200) {
        const res: Response = response.data;
        setResults(res.results); // Keep original data
        setData(res);

        // Debug: Log anchor_keys to verify they're populated
        console.log("Search response anchor_keys:", res.anchor_keys);
        console.log("Search response data:", res);

        // Build anchor_keys from results if not provided by backend or if they contain only None values
        let anchorKeysToSend = res.anchor_keys;
        
        // Check if anchor_keys are empty or contain only None/empty values
        const hasValidAnchorKeys = anchorKeysToSend && 
          anchorKeysToSend.length > 0 && 
          anchorKeysToSend.some((key: any) => key?.player_name || key?.stat_value || key?.game_date);

        if (!hasValidAnchorKeys) {
          // Extract up to 10 items from results as anchor keys
          anchorKeysToSend = res.results.slice(0, 10).map(result => ({
            player_name: result.metadata.PLAYER,
            stat_value: Object.values(result.stats)[0], // Get first stat value
            game_date: result.metadata.DATE
          }));
          console.log("Built anchor_keys from results (backend provided empty):", anchorKeysToSend);
        } else {
          console.log("Using anchor_keys from search response:", anchorKeysToSend);
        }

        dashboard_Service.getVisualization({
          BasicQuery: searchQuery,
          SportCode: selectedSport,
          Entity: config["entity"] || "Player",
          TeamCode: teamCode,
          Filters: filters || {},
          aql_output: res.aql_output,
          anchor_keys: anchorKeysToSend,
        }).then((vizResponse) => {
          setVisualizationData(vizResponse.data);
          // console.log("Viz data ready in background:", vizResponse.data);
        }).catch((err) => {
          const detail = err?.response?.data?.detail;
          const message = typeof detail === "string"
            ? detail
            : "Visualization is not available for this query.";
          setVisualizationData({ error: true, message });
          console.error("Background viz fetch failed:", err);
        });
        
     
        const count = Number(response?.data.count);
        setMessage(`${count} Results found`);
        if (count < payload["PageSize"]) {
          setTotalPages(1);
        } else {
          const pageCount = Math.ceil(count / payload["PageSize"]);
          setTotalPages(pageCount);
        }
        setLoading(false);
        setResultsLoaded(true); // add this
        setIsDisabled(false);

        if (localStorage.getItem('tourStep') === 'navigateToResults' && res.results.length > 0) {
          console.log("Starting result tour - navigated from dashboard");
          const tourCookieName = `resultTour_${user?.username}`;
          const resultTourCompleted = Cookies.get(tourCookieName);

          if (!resultTourCompleted) {
            setTimeout(() => {
              setRunResultTour(true);
            }, 1000);
          }
          localStorage.setItem('tourStep', 'selectFilter');
        }
      } else {
        setLoading(false);
        setError(response?.data.error);
        setMessage("0 Matching Results Found!");
        setIsDisabled(true);
        if (localStorage.getItem('tourStep') === 'navigateToResults') {
          localStorage.removeItem('tourStep'); // Tour can't continue if no results
        }
      }
    } catch (error: any) {
      console.error("Error in fetching Results:", error);

      let errorMessage = "0 Matching Results Found!";
      const errorStatus = error?.response?.status || 0;
      setErrorStatus(errorStatus); // <-- Save it in state
      console.log("error status is", errorStatus)

      if (
        errorStatus === 403 &&
        (error?.response?.data?.detail?.code === "TRIAL_EXPIRED" ||
          error?.response?.data?.detail?.code === "SUBSCRIPTION_EXPIRED" ||
          error?.response?.data?.detail?.code === "TEAM_SEAT_OVERFLOW")
      ) {
        // Global interceptor opens the relevant modal; don't show a misleading "no results" state.
        setLoading(false);
        setResultsLoaded(false);
        setIsDisabled(true);
        return;
      }

      if (errorStatus >= 500) {
        errorMessage = "A server error occurred. Please try again later or contact support.";
        // Optionally: log this error to an error reporting service
      } else if (errorStatus === 404) {
        errorMessage = "No results found for your query.";
      } else if (errorStatus === 401) {
        errorMessage = "You are not authorized to perform this action.";
      } else if (errorStatus === 400) {
        errorMessage = "Bad request. Please check your input and try again.";
      } else if (!errorStatus) {
        errorMessage = "Network error. Please check your connection.";
      }

      setError(errorMessage);
      setMessage(errorMessage);
      setLoading(false);
      setIsDisabled(true);

      if (localStorage.getItem('tourStep') === 'navigateToResults') {
        localStorage.removeItem('tourStep');
      }
    }
  };

  console.log("Rendering ResultScreen with config:", visualizationData);

  const basicQuery = visualizationData?.basic_query || searchString || "";

  function handleButtonClick() {
    console.log("clicked");
  }

  useEffect(() => {
    let config = localStorageService.getNLPFilter();
    let searchFilters = localStorageService.getSearchFilter();
    let sortFields = localStorageService.getSortFields();

    if (config && config !== "{}") { // Check if config is not null/empty before parsing
      try {
        const parsedConfig = typeof config === 'string' ? JSON.parse(config) : config;
        setfilter_config(parsedConfig);
        setSearchString(parsedConfig.searchString || "");

        const parsedSearchFilters = typeof searchFilters === 'string' ? JSON.parse(searchFilters) : searchFilters;
        setSelectedFilters(parsedSearchFilters || {});
        setAppliedFilters(parsedSearchFilters || {});

        const parsedSortFields = typeof sortFields === 'string' ? JSON.parse(sortFields) : sortFields;
        setSortFields(parsedSortFields || { sortBy: "", sortOrder: "" });

        getResults(parsedConfig, parsedSearchFilters || {}, parsedSortFields || {});
      } catch (e) {
        console.error("Error parsing localStorage data:", e);
        setLoading(false); // Stop loading if there's an error with config
      }
    } else {
      console.warn("No NLP filter config found in localStorage.");
      setLoading(false); // Stop loading if no config
      // Potentially navigate back or show a message if config is essential
      // navigate(-1);
      setMessage("Search configuration not found. Please start a new search.");
    }

    // getResults(config, searchFilters, sortFields);
  }, []);

  const handleDropdownChange = (
    key: "OPP" | "POS" | "CLASS" | "PLAYER" | "RES" | "CONF" | "MATCH_TYPE" | "PERIOD_TYPE" | "GAME_TYPE",
    selectedValue: any,
  ) => {
    setSelectedFilters((prevFilters: any) => {
      const updatedFilters = { ...prevFilters };
      let newValue = key === "RES" ? String(selectedValue.code) : selectedValue;

      // Get current values for this filter
      const currentValues = updatedFilters[key] || [];

      if (currentValues.includes(newValue)) {
        // Remove the value if it already exists
        updatedFilters[key] = currentValues.filter(
          (item: any) => item !== newValue,
        );
      } else {
        // else add the new value
        updatedFilters[key] = [...currentValues, newValue];
      }

      // --- Advance Joyride if filter is selected ---
      // if (localStorage.getItem('tourStep') === 'selectFilter' && runResultTour) {
      //   localStorage.setItem('tourStep', 'applyFilter');
      // }
      // ---

      return updatedFilters;
    });
  };

  const handleStartDateChange = (newDate: string) => {
    setStartDate(newDate);
    setSelectedFilters((prevFilters: any) => {
      const updatedFilters = { ...prevFilters, DATE_MIN: newDate };

      // If the selected startDate is after the selected endDate, reset endDate
      if (endDate && new Date(newDate) > new Date(endDate)) {
        setEndDate(null); // Reset endDate if it's before the new start date
        delete updatedFilters["DATE_MAX"]; // Remove endDate if it exists
      }
      return updatedFilters;
    });
  };

  const handleEndDateChange = (newDate: string) => {
    setEndDate(newDate);
    setSelectedFilters((prevFilters: any) => ({
      ...prevFilters,
      DATE_MAX: newDate,
    }));
  };

  const handleApplyFilters = async (newSort?: any) => {
    localStorageService.addSearchFilter(await selectedFiltersRef.current);
    localStorageService.addSortFields(sortFields)
    setAppliedFilters(await selectedFiltersRef.current);

    setCurrentPage(1);
    let newConfig = {
      ...filter_config,
      "PageNumber": Number(1),
      "PageSize": Number(itemsPerPage)
    }
    // debugger;
    console.log("new config: ", JSON.parse(JSON.stringify(newConfig)));
    getResults(newConfig, await selectedFiltersRef.current, newSort ?? sortFields);
    setfilter_config(newConfig);

    // --- Advance or End Joyride after applying filters ---
    // if (localStorage.getItem('tourStep') === 'applyFilter' && runResultTour) {
    // This would be the last action, Joyride's callback will handle 'finished' status
    // setRunResultTour(false); // Or let the callback handle it
    //   localStorage.setItem('searchTourCompleted', 'true');
    //   localStorage.removeItem('tourStep');
    // }
    // ---
  }

  const handleClearAllFilters = () => {
    console.warn("RESETTING FILTERS AND DATES");
    setSelectedFilters({});
    setStartDate(null);
    setEndDate(null);
    setCurrentPage(1);

    let newConfig = {
      ...filter_config,
      PageNumber: 1,
      PageSize: Number(itemsPerPage),
    };

    getResults(newConfig, {}, sortFields);
    setfilter_config(newConfig);
  };

  const handleExport = async (e: React.FormEvent): Promise<void> => {
    e.stopPropagation();

    // 👉 Pass sportCode and entity to the export function
    exportToXLSX(
      results,
      'exported_data.xlsx',
      selectedSport,
      filter_config?.Entity || "Player"
    );

    // Log the export operation
    try {
      if (data?.sql_query) {
        await dashboard_Service.logExport(
          searchString,
          selectedSport,
          filter_config?.Entity || "Player",
          results.length
        );
        console.log('Export logged successfully');
      }
    } catch (error) {
      console.error('Error logging export:', error);
    }

    openToast("Results data has been downloaded", "green");
  };

  async function handleReportIssue(e: React.FormEvent) {
    e.preventDefault();

    try {
      const res: AxiosResponse = await dashboard_Service.reportIssue({
        ...data,
        Issue: issue,
        SearchQuery: searchString,
      });

      if (res.status >= 200 && res.status < 300) {
        setFeedbackMessage(res.data.Message); // "Issue reported successfully"
        setFeedbackType("success");
        // Close modal after successful report
        setTimeout(() => {
          setIsModalOpen(false);
          // Reset form and feedback states
          setIssue("");
          setFeedbackMessage(null);
          setFeedbackType(null);
        }, 1500); // Give user 1.5 seconds to see success message
      } else {
        setFeedbackMessage("Something went wrong. Please try again.");
        setFeedbackType("error");
        // Keep modal open for error messages
      }
    } catch (error: any) {
      const msg = error.response?.data?.detail || "Failed to report issue.";
      setFeedbackMessage(msg);
      setFeedbackType("error");
      // Keep modal open for error messages
    }
  }

  return (
    <>
      <Joyride
        steps={resultTourSteps}
        run={runResultTour}
        continuous={true}
        showProgress={true}
        showSkipButton={true}
        callback={handleResultJoyrideCallback}
        styles={{
          options: {
            zIndex: 10000,
            primaryColor: team.colorCode,
          }
        }}
        spotlightClicks={true}
        locale={{
          last: "Finish"
        }}    // disableOverlay={true}
      />
      <div className="w-full flex flex-col gap-8">
        {/* Modal */}
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <h2 className="text-xl font-semibold text-center">Report Issue</h2>

          <form
            className="flex flex-col justify-center items-center gap-4 mt-2"
            onSubmit={handleReportIssue}
          >
            <label className="text-gray-600">
              Please describe the issue you are facing
            </label>

            <input
              type="text"
              placeholder="Enter your issue..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              value={issue}
              onChange={(e) => setIssue(e.target.value)}
            />

            {/* Success / Error message block */}
            {feedbackMessage && (
              <div
                className={`w-full text-center font-medium p-2 rounded-md ${feedbackType === "success"
                    ? "text-green-700 bg-green-100 border border-green-400"
                    : "text-red-700 bg-red-100 border border-red-400"
                  }`}
              >
                {feedbackMessage}
              </div>
            )}

            <div className="flex flex-row justify-between gap-4 w-full">
              <button
                type="button"
                onClick={() => {
                  setIsModalOpen(false);
                  setFeedbackMessage(null);
                  setFeedbackType(null);
                }}
                className="w-full px-4 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500"
              >
                Close
              </button>
              <button
                type="submit"
                className="w-full px-4 py-2 bg-[#35B8D7] text-white rounded-lg"
              >
                Report
              </button>
            </div>
          </form>
        </Modal>

        <div className="bg-gradient-to-t from-[#E8ECF4] to-[#FFFFFF] flex flex-row gap-2 items-center justify-center w-full py-5 px-12 ">
          <div
            className="h-full rounded-xl flex flex-row items-center justify-center gap-2 cursor-pointer"
            onClick={() => {
              navigate(-1);
            }}
          >
            <img src="/arrow_left.png" alt="filter icon" />
          </div>
          {filter_config ? (
              <NLPSearch
              filterdata={filter_config}
              searchString={searchString}
              setSearchString={setSearchString}
              handleSearch={() =>
                getResults(
                  { ...filter_config, PageNumber: 1 },
                  selectedFiltersRef.current,
                  sortFields,
                  true
                )
              }
              resetFilters={() => { console.warn("RESETTING FILTERS AFTER SEARCH"); setSelectedFilters({}) }}
              resetSorting={() => setSortFields({})}
              // 5. PASS NEW PROPS
              suggestions={suggestions}
              isPending={loading}
              onSelectSuggestion={(val: string) => {
                setSearchString(val);
                setSuggestions([]);
              }}
            />
          ) : (
            ""
          )}
        </div>
        <div className="flex flex-col gap-4 px-10 pt-2 bg-gradient-to-t from-[#E3E8F5] to-[#F6F9FC]">
          <div></div>
          <div className="flex flex-row gap-2 items-center justify-between flex-wrap">
            <p className="font-primary font-semibold text-lg">
              {loading === false ? message : "Searching..."}
            </p>
            {/* {JSON.stringify(selectedFilters)} */}
            <div className="flex flex-row gap-2 items-center justify-end flex-wrap">



            <Button
              color={team.colorCode}
              isDisabled={isDisabled || !resultsLoaded || loading || visualizationData === null}
              text="Visualize Data"
              icon="/chart-bar-solid-full.svg" 
              iconAllignment="left"
              handleButtonClick={() => {
                if (!visualizationData) {
                  openToast("Visualization is still preparing. Please wait a moment.", "red");
                  return;
                }
                if (visualizationData?.error) {
                  setIsGraphPopupOpen(true);
                  return;
                }
                setIsGraphPopupOpen(true);
              }}

            />










              <Button
                handleButtonClick={() => setIsModalOpen(true)}
                color={team.colorCode}
                isDisabled={isDisabled}
                text="Report Issue"
                icon={isDisabled ? "/graph_icon_grey.png" : "/graph_icon.png"}
                type=""
                iconAllignment="left"
                size=""
              />
              <Button
                color={team.colorCode}
                isDisabled={isDisabled}
                text="Export"
                icon={isDisabled ? "/export_icon_grey.png" : "/export_icon.png"}
                type=""
                iconAllignment="left"
                size=""
                handleButtonClick={handleExport}
              />
              {/* <Button
                handleButtonClick={handleButtonClick}
                color={team.colorCode}
                isDisabled={isDisabled}
                text="Copy"
                icon={isDisabled ? "/copy_icon_grey.png" : "/copy_icon.png"}
                type=""
                iconAllignment="left"
                size=""
              /> */}
            </div>
          </div>
          <hr className="h-px my-0.5 bg-[#AEAEAE] border-0"></hr>
          {loading === true ? (
            <div className="w-full flex flex-row justify-center items-center p-2">
              <Spinner />
            </div>
          ) : (
            <div className="flex flex-row gap-5 mb-2">
              {/* Filter panel */}
              {data?.aql_output?.query_type !== "streak" ? (
                <div
                  className="max-w-[270px] w-full  bg-white rounded-[10px] border border-[#DEE7F4] p-4 mt-1.5
                        flex flex-col gap-2 overflow-y-auto custom-scrollbar h-fit"
                  id="tour-filter-opponent"
                >
                  <div className="flex flex-row justify-between">
                    <p className="text-md font-semibold font-primary">Filters</p>
                    <p
                      className="text-sm font-semibold font-primary cursor-pointer"
                      style={{
                        color: team.colorCode,
                      }}
                      onClick={() => {
                        console.warn("RESETTING FILTERS AND DATES");
                        setSelectedFilters({});
                        setStartDate(null); // Reset start date
                        setEndDate(null);   // Reset end date

                        // Clear the results and reload with original data
                        setCurrentPage(1);
                        let newConfig = {
                          ...filter_config,
                          "PageNumber": Number(1),
                          "PageSize": Number(itemsPerPage)
                        }

                        // Call getResults with empty filters to show original unfiltered data
                        getResults(newConfig, {}, sortFields);
                        setfilter_config(newConfig);

                      }}
                    >
                      Clear All
                    </p>
                  </div>




                  {/* Opponent Conference - HIDE if no teams available or only 1 unique opponent */}
                  {filter_config?.entity !== "Player" && filter_config?.entity !== "Team" || shouldShowFilter("CONF", data?.aql_output?.stat_period) ? (
                    Object.keys(data?.conferences ?? {}).length > 0 && (
                      <div className="flex flex-col gap-[7px]">
                        <p className="text-sm font-secondary font-semibold text-[#5B5B5B]">
                          Opponent Conference
                        </p>
                        <MultiSelectDropdown
                          options={Object.keys(data?.conferences ?? {})}
                          options2={[]}
                          def={"Select Opponent Conference"}
                          setSelectedOption={(key: any, selectedValue: any) =>
                            handleDropdownChange(key, selectedValue)
                          }
                          selectedOption={selectedFilters}
                          filterKey={"CONF"}
                          disabled={false}
                        />
                      </div>
                    )
                  ) : null}

                  {/* Opponents - HIDE if no teams or only 1 unique opponent */}
                  {filter_config?.entity !== "Player" && filter_config?.entity !== "Team" || shouldShowFilter("OPP", data?.aql_output?.stat_period) ? (
                    getOpponentOptions().length > 1 && (
                      <div className="flex flex-col gap-[7px]">
                        <p className="text-sm font-secondary font-semibold text-[#5B5B5B]">
                          Opponents
                        </p>
                        <MultiSelectDropdown
                          options={getOpponentOptions()}
                          options2={[]}
                          def={"Select Opponent"}
                          setSelectedOption={(key: any, selectedValue: any) =>
                            handleDropdownChange(key, selectedValue)
                          }
                          selectedOption={selectedFilters}
                          filterKey={"OPP"}
                          disabled={false}
                        />
                      </div>
                    )
                  ) : null}


                  {/* Results - HIDE FOR PLAYERS IN SEASON/CAREER */}
                  {filter_config?.entity !== "Player" && filter_config?.entity !== "Team" || shouldShowFilter("RES", data?.aql_output?.stat_period) ? (
                    <div className="flex flex-col gap-[7px]">
                      <p className="text-sm font-secondary font-semibold text-[#5B5B5B]">
                        Results
                      </p>
                      <MultiSelectDropdown
                        options={gameResults}
                        options2={[]}
                        def={"Select Result"}
                        setSelectedOption={(key: any, selectedValue: any) =>
                          handleDropdownChange(key, selectedValue)
                        }
                        selectedOption={selectedFilters}
                        filterKey={"RES"}
                        disabled={false}
                      />
                    </div>
                  ) : null}



                  {data?.game_type?.length > 0 && <div className="flex flex-col gap-[7px]">
                    <p className="text-sm font-secondary font-semibold text-[#5B5B5B]">
                      Game Type
                    </p>
                    <MultiSelectDropdown
                      options={data?.game_type ?? []}
                      options2={[]}
                      def={"Select Game Type"}
                      setSelectedOption={(key: any, selectedValue: any) =>
                        handleDropdownChange(key, selectedValue)
                      }
                      selectedOption={selectedFilters}
                      filterKey={"GAME_TYPE"}
                      disabled={false}
                    />
                  </div>}


                  {data?.period_type?.length > 0 && <div className="flex flex-col gap-[7px]">
                    <p className="text-sm font-secondary font-semibold text-[#5B5B5B]">
                      Period Type
                    </p>
                    <MultiSelectDropdown
                      options={data?.period_type ?? []}
                      options2={[]}
                      def={"Select Period Type"}
                      setSelectedOption={(key: any, selectedValue: any) =>
                        handleDropdownChange(key, selectedValue)
                      }
                      selectedOption={selectedFilters}
                      filterKey={"PERIOD_TYPE"}
                      disabled={false}
                    />
                  </div>}



                  {/* input data */}
                  {filter_config?.entity === "Player" && <div className="flex flex-col gap-[7px]">
                    <p className="text-sm font-secondary font-semibold text-[#5B5B5B]">
                      Name
                    </p>
                    <SearchWithSuggestions
                      onSelect={setInputValue}
                      handleDropdownChange={handleDropdownChange}
                    />
                  </div>}



                  {filter_config?.entity === "Player" ? (
                    <>

                      <div className="flex flex-col gap-[7px]">
                        <p className="text-sm font-secondary font-semibold text-[#5B5B5B]">
                          Position
                        </p>
                        <MultiSelectDropdown
                          options={data?.positions ?? []}
                          options2={[]}
                          def={"Select Positions"}
                          setSelectedOption={(key: any, selectedValue: any) =>
                            handleDropdownChange(key, selectedValue)
                          }
                          selectedOption={selectedFilters}
                          filterKey={"POS"}
                          disabled={false}
                        />
                      </div>

                      <div className="flex flex-col gap-[7px]">
                        <p className="text-sm font-secondary font-semibold text-[#5B5B5B]">
                          Classes
                        </p>
                        <MultiSelectDropdown
                          options={data?.classes ?? []}
                          options2={[]}
                          def={"Select Classes"}
                          setSelectedOption={(key: any, selectedValue: any) =>
                            handleDropdownChange(key, selectedValue)
                          }
                          selectedOption={selectedFilters}
                          filterKey={"CLASS"}
                          disabled={false}
                        />
                      </div>
                    </>
                  ) : (
                    ""
                  )}


                  {/* Date Filter - HIDE FOR PLAYERS IN SEASON/CAREER */}
                  {filter_config?.entity !== "Player" && filter_config?.entity !== "Team" || shouldShowFilter("DATE_MIN", data?.aql_output?.stat_period) ? (
                    <div className="flex flex-col gap-[7px]">
                      <p className="text-sm font-secondary font-semibold text-[#5B5B5B]">
                        Date
                      </p>
                      <div className="flex flex-row justify-between flex-wrap gap-1">
                        <Datepicker
                          date={startDate || ""}
                          setDate={handleStartDateChange}
                          disabled={false}
                          type="DATE_MIN"
                          className="w-[114px]"
                        />
                        <Datepicker
                          date={endDate || ""}
                          setDate={handleEndDateChange}
                          disabled={!startDate}
                          type="DATE_MAX"
                          startDate={startDate || undefined}
                          className="w-[114px]"
                        />
                      </div>
                    </div>
                  ) : null}

                  <button
                    className="pt-[0.75rem] pb-[0.75rem] pl-[2.8125rem] pr-[2.8125rem] bg-[#A8042D] text-white rounded-[0.5rem] text-sm font-medium"
                    onClick={handleApplyFilters}
                    id="tour-apply-filters-button"
                  >
                    Apply filters
                  </button>
                  {/* Show Game Date Picker if the selectedPeriod is gamel */}
                  {filter_config?.selectedPeriod === "Game" ? (
                    <div className="flex flex-col gap-[7px]">
                      <label className="text-sm font-secondary font-semibold text-[#5B5B5B]">
                        Game Date
                      </label>
                      {/*Start Date*/}
                      <div className="flex flex-row justify-between flex-wrap gap-1">
                        <Datepicker
                          date={startDate || ""}
                          setDate={handleStartDateChange}
                          disabled={false}
                          type="DATE_MIN"
                          className="w-[114px]"
                        />
                        {/*End Date*/}
                        <Datepicker
                          date={endDate || ""}
                          setDate={handleEndDateChange}
                          disabled={!startDate} // Disable end date until start date is selected
                          startDate={startDate || undefined} // Pass startDate to endDate to calculate min date
                          type="DATE_MAX"
                          className="w-[114px]"
                        />
                      </div>
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              ) : (
                ""
              )}
              {/* Table and content */}
              <div className="flex-grow overflow-x-auto flex flex-col gap-2">
                {/* Render the filter boxes */}
                <FilterTags
                  onClick={handleApplyFilters}
                  selectedFilters={selectedFilters}
                  appliedFilters={appliedFilters}
                  setAppliedFilters={setAppliedFilters}
                  selectedFiltersRef={selectedFiltersRef}
                  setSelectedFilters={setSelectedFilters}
                  result={gameResults}
                  team={team}
                  setStartDate={setStartDate}
                  setEndDate={setEndDate}
                />
                {(data?.results?.length ?? 0 > 0) ? (
                  <Table
                    data={data?.results}
                    fullData={data}
                    sortFields={sortFields}
                    setSortFields={setSortFields}
                    handleSortChange={handleApplyFilters}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                  />
                ) : (
                  <NoDataFound
                    query={error}
                    errorStatus={errorStatus}
                    onReportClick={() => {
                      setIssue(`Received ${errorStatus} error during search: "${searchString}"`);
                      setIsModalOpen(true);
                    }}
                  />
                )}
                {totalPages > 0 && data?.results && data?.results.length > 0 ? (
                  <Pagination
                    showItemsPerPage={true}
                    totalPages={totalPages}
                    currentPage={currentPage}
                    onPageChange={onPageChange}
                    itemsPerPage={itemsPerPage}
                    onItemsPerPageChange={onItemsPerPageChange}
                  />
                ) : (
                  ""
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Visualization Loading Spinner */}
      {vizLoading && <VisualizationSpinner teamColor={team.colorCode} />}

      {/* Graph Popup */}


      <GraphPopupModal
        isOpen={isGraphPopupOpen}
        onClose={() => setIsGraphPopupOpen(false)}
        visualizationData={visualizationData}
        title={basicQuery}
        teamColor={team.colorCode}
      />


    </>
  );
}

//filter tag component
function FilterTags({
  selectedFilters,
  setSelectedFilters,
  appliedFilters,
  setAppliedFilters,
  selectedFiltersRef,
  result,
  team,
  setStartDate,
  setEndDate,
  onClick,
}: {
  selectedFilters: { [key: string]: any },
  setSelectedFilters: any,
  appliedFilters: { [key: string]: any },
  setAppliedFilters: any,
  selectedFiltersRef: React.RefObject<any>
  team: any,
  result: { name: string; code: string }[],
  setStartDate: any,
  setEndDate: any,
  onClick: any,
}) {
  const resultMap = useMemo(() => {
    return result.reduce(
      (map, team) => {
        map[team.code] = team.name;
        return map;
      },
      {} as { [key: string]: string },
    );
  }, [result]);

  // Check if a specific filter value has been applied
  const isFilterApplied = (key: string, value: any): boolean => {
    if (key === "DATE_MIN" || key === "DATE_MAX") {
      return appliedFilters[key] === value;
    }
    return appliedFilters[key]?.includes(value);
  };

  const handleRemove = (key: string, value: any, isApplied: boolean) => {
    // Always remove from selectedFilters
    setSelectedFilters((prevFilters: any) => {
      const updatedFilters = { ...prevFilters };

      try {
        if (key === "DATE_MIN") {
          delete updatedFilters[key];
          delete updatedFilters["DATE_MAX"];
          setStartDate("");
          setEndDate("");
        } else if (key === "DATE_MAX") {
          delete updatedFilters[key];
          setEndDate("");
        } else if (Array.isArray(updatedFilters[key])) {
          updatedFilters[key] = updatedFilters[key].filter((_res: any) => _res != String(value));
          if (!updatedFilters[key].length) delete updatedFilters[key];
        }
      } catch (e) {
        console.error("could not remove !! ", e);
      }

      return updatedFilters;
    });

    // If this filter was applied, also remove from appliedFilters and make API call
    if (isApplied) {
      setAppliedFilters((prevFilters: any) => {
        const updatedFilters = { ...prevFilters };

        try {
          if (key === "DATE_MIN") {
            delete updatedFilters[key];
            delete updatedFilters["DATE_MAX"];
          } else if (key === "DATE_MAX") {
            delete updatedFilters[key];
          } else if (Array.isArray(updatedFilters[key])) {
            updatedFilters[key] = updatedFilters[key].filter((_res: any) => _res != String(value));
            if (!updatedFilters[key].length) delete updatedFilters[key];
          }
        } catch (e) {
          console.error("could not remove from applied filters !! ", e);
        }

        return updatedFilters;
      });

      // Make API call only if filter was previously applied
      onClick();
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {Object.entries(selectedFilters).map(([key, values]) => {
        const displayKey =
          key === "DATE_MIN"
            ? "Start Date"
            : key === "DATE_MAX"
              ? "End Date"
              : key.toUpperCase();

        if (key === "DATE_MIN" || key === "DATE_MAX") {
          const isApplied = isFilterApplied(key, values); // CHECK IF APPLIED
          return (
            <div
              key={key}
              className="px-3 py-1 bg-white text-sm font-primary font-medium rounded-[10px] flex items-center gap-2 cursor-pointer border"
              style={{
                color: team.colorCode,
                borderColor: team.colorCode,
              }}
              onClick={() => handleRemove(key, values, isApplied)} // PASS isApplied
            >
              {displayKey}: {values}
              <span className="text-lg font-bold">&times;</span>
            </div>
          );
        }

        return values.map((value: any, i: number) => {
          const isApplied = isFilterApplied(key, value); // CHECK IF APPLIED
          return (
            <div
              key={`${key}-${value}-${i}`}
              className="px-3 py-1 bg-white text-sm font-primary font-medium rounded-[10px] flex items-center gap-2 cursor-pointer border"
              style={{
                color: team.colorCode,
                borderColor: team.colorCode,
              }}
              onClick={() => handleRemove(key, value, isApplied)} // PASS isApplied
            >
              {displayKey}: {key === "RES" ? resultMap[value] || value : value}
              <span className="text-lg font-bold">&times;</span>
            </div>
          );
        });
      })}
    </div>
  );
}
